/**
 * @lodex-pro/shared/abbreviation — the one rule for hauler, operator and customer abbreviations
 * (OPEN-203). A haul ID is {HAULER}-{OPERATOR}{9 digits}, so the code is letters only: a digit
 * would run into the ticket number. Corbin typed WAV3, read it as four letters, and the old "2–4
 * letters" never said why it was refused.
 *
 * Every abbreviation form (create tenant, tenant detail, operator detail, add operator, express
 * customer, the Abbreviations page, dispatch's customer form and setup wizard) uses this text and
 * check; the API's validators use the same sentence. Browsers import the `./abbreviation` subpath.
 */

/** Letters only, 2 to 4 of them (either case: every writer uppercases). */
export const ABBREVIATION_RE = /^[A-Za-z]{2,4}$/;

/** The rule, in the words every form shows. */
export const ABBREVIATION_RULE_TEXT =
  "Letters only (A–Z), 2 to 4 of them. Numbers aren't allowed because this goes in front of the ticket number.";

/** Null when `value` (trimmed) is a valid abbreviation, else the rule text. */
export function abbreviationError(value: string): string | null {
  return ABBREVIATION_RE.test(value.trim()) ? null : ABBREVIATION_RULE_TEXT;
}

/**
 * A starting suggestion for a company name whose own letters do not lead it ("84 ENERGY" →
 * "ENER", "007 OPERATING" → "OPER"): the first four letters of the first word that has any,
 * topped up from the next words. Null when the name has fewer than two letters.
 */
export function suggestAbbreviation(name: string): string | null {
  const letters = name
    .toUpperCase()
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Z]/g, ''))
    .filter((w) => w !== '')
    .join('');
  return letters.length >= 2 ? letters.slice(0, 4) : null;
}

/** True when the name starts with a digit, where a suggestion helps most. */
export function nameStartsWithDigit(name: string): boolean {
  return /^\s*\d/.test(name);
}
